<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
} from '../helpers';

const props = defineProps({
  campaign: { type: Object, required: true },
});
const { t, locale } = useI18n();

const metrics = computed(() => props.campaign.metrics || {});
const progress = computed(
  () => metrics.value.progress || { processed: 0, total: 0, percent: 0 }
);
const percent = computed(() =>
  Math.min(100, Math.max(0, progress.value.percent || 0))
);
const paused = computed(() => props.campaign.status === 'paused');
const num = v => formatNumber(v, locale.value);

const stats = computed(() => [
  {
    key: 'SPEED',
    value: t('WASAPI_CAMPAIGNS.DETAIL.PROGRESS.SPEED_VALUE', {
      value: metrics.value.speed_per_minute ?? 0,
    }),
  },
  {
    key: 'ETA',
    value: formatDuration(metrics.value.eta_minutes, t),
  },
  {
    key: 'LAST_SENT',
    value: formatDateTime(metrics.value.last_sent_at, locale.value),
  },
  {
    key: 'NEXT_WINDOW',
    value: metrics.value.next_window_opening
      ? formatDateTime(metrics.value.next_window_opening, locale.value)
      : '—',
  },
]);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{
          paused
            ? t('WASAPI_CAMPAIGNS.DETAIL.PROGRESS.TITLE_PAUSED')
            : t('WASAPI_CAMPAIGNS.DETAIL.PROGRESS.TITLE')
        }}
      </h3>
      <span class="text-sm tabular-nums text-n-slate-11">
        {{
          t('WASAPI_CAMPAIGNS.DETAIL.PROGRESS.OF', {
            processed: num(progress.processed),
            total: num(progress.total),
          })
        }}
        · {{ formatPercent(percent, locale) }}
      </span>
    </div>
    <div class="w-full h-2.5 rounded-full bg-n-alpha-2 overflow-hidden">
      <div
        class="h-full rounded-full transition-all duration-500"
        :class="paused ? 'bg-n-amber-9' : 'bg-n-brand'"
        :style="{ width: `${percent}%` }"
      />
    </div>
    <p
      v-if="metrics.next_window_opening && !paused"
      class="text-xs text-n-amber-11"
    >
      {{
        t('WASAPI_CAMPAIGNS.DETAIL.PROGRESS.WAITING_WINDOW', {
          date: formatDateTime(metrics.next_window_opening, locale),
        })
      }}
    </p>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div v-for="stat in stats" :key="stat.key" class="flex flex-col">
        <span class="text-xs text-n-slate-11">{{
          t(`WASAPI_CAMPAIGNS.DETAIL.PROGRESS.${stat.key}`)
        }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          stat.value
        }}</span>
      </div>
    </div>
  </div>
</template>
